import { toast } from 'react-toastify';
import api from '../api';

/**
 * Sensitive fields to remove from payloads before logging
 */
const SENSITIVE_FIELDS = [
    'password', 'pinCode', 'token', 'twoFactorToken',
    'secret', 'refreshToken', 'accessToken', 'authorization'
];

/**
 * Sanitizes an object by removing sensitive fields
 */
function sanitizePayload(obj, seen = new Set()) {
    if (!obj || typeof obj !== 'object') return obj;
    if (seen.has(obj)) return '[Circular]';
    seen.add(obj);

    if (Array.isArray(obj)) {
        return obj.map(item => sanitizePayload(item, seen));
    }

    const sanitized = {};
    for (const [key, value] of Object.entries(obj)) {
        if (SENSITIVE_FIELDS.some(field => key.toLowerCase().includes(field.toLowerCase()))) {
            sanitized[key] = '[REDACTED]';
        } else if (value && typeof value === 'object') {
            sanitized[key] = sanitizePayload(value, seen);
        } else {
            sanitized[key] = value;
        }
    }
    return sanitized;
}

/**
 * Gets common error log data
 */
function getCommonData() {
    return {
        frontendUrl: window.location.pathname,
        userAgent: navigator.userAgent
    };
}

/**
 * Sends error log to backend (fire-and-forget)
 */
async function sendErrorLog(data) {
    try {
        await api.post('/error-logs', data);
    } catch (error) {
        // Silently fail - don't cause cascading errors
        console.debug('Failed to log error:', error);
    }
}

/**
 * Logs a server/API error (from axios interceptor)
 * @param {Error} axiosError - The axios error object
 */
export function logServerError(axiosError) {
    const { config, response } = axiosError;

    // Routes answer with either { error } (createError, used across playback) or
    // { message }. Reading only one of them logged every playback failure as the
    // generic "Request failed with status code 500", which also collapsed
    // unrelated failures under a single grouping hash.
    const bodyMessage = [response?.data?.error, response?.data?.message]
        .find(value => typeof value === 'string' && value.trim());

    const data = {
        ...getCommonData(),
        errorType: 'server',
        message: bodyMessage || axiosError.message || 'Unknown server error',
        requestUrl: config?.url,
        requestMethod: config?.method?.toUpperCase(),
        requestPayload: config?.data ? sanitizePayload(
            typeof config.data === 'string' ? JSON.parse(config.data) : config.data
        ) : undefined,
        responseStatus: response?.status,
        // A request that never got a response (network drop, timeout, blocked
        // preflight) has no body to describe it — keep axios' own code instead.
        responseData: response?.data
            ? sanitizePayload(response.data)
            : (axiosError.code ? { code: axiosError.code } : undefined)
    };

    sendErrorLog(data);
}

/**
 * Logs a toast error message
 * @param {string} message - The error message shown in toast
 */
export function logToastError(message) {
    const data = {
        ...getCommonData(),
        errorType: 'toast',
        message: typeof message === 'string' ? message : String(message)
    };

    sendErrorLog(data);
}

/**
 * Flattens toast content — which may be a plain string or a React node — into
 * the text the user actually read.
 */
function toastText(content) {
    if (typeof content === 'string') return content.trim();
    if (typeof content === 'number') return String(content);
    if (Array.isArray(content)) return content.map(toastText).filter(Boolean).join(' ');
    if (content?.props?.children) return toastText(content.props.children);
    return '';
}

/**
 * Subscribes to every error toast the app raises, wherever it was raised from.
 *
 * Logging used to depend on each call site importing `errorToast`, so the ones
 * calling `toast.error` directly — the whole playback surface among them — never
 * reached the error log. Listening on the toast stream itself removes that
 * dependency: an error the user is shown is an error that gets recorded.
 *
 * @returns {() => void} unsubscribe
 */
export function installToastErrorLogging() {
    return toast.onChange((payload) => {
        if (payload.status !== 'added' || payload.type !== 'error') return;

        const message = toastText(payload.content);
        if (message) logToastError(message);
    });
}

/**
 * Logs an uncaught exception
 * @param {Error} error - The error object
 * @param {Object} errorInfo - Additional error info (e.g., from React error boundary)
 */
export function logException(error, errorInfo) {
    if (!error) return;

    const data = {
        ...getCommonData(),
        errorType: 'exception',
        message: error.message || String(error),
        stack: error.stack || (errorInfo?.componentStack)
    };

    sendErrorLog(data);
}
